import * as React from 'react';

/**
 * Whether a CSS media query matches, kept current as it changes.
 *
 * It is what switches a `DataTable` to cards and a `FilterBar` into a bottom
 * sheet below `md`, and what makes the nav drawer `inert` below `lg` (spec R1,
 * R3 and R6 of the 2026-10 mobile design critique). Markup that differs by
 * width needs this; a mere style difference should stay a Tailwind variant.
 *
 * `defaultValue` is the answer when `window.matchMedia` does not exist — jsdom,
 * and therefore every unit test that does not stub it. Callers pass the DESKTOP
 * answer (`useMediaQuery('(min-width: 768px)', true)`), so a test that knows
 * nothing about breakpoints renders the layout it was written for.
 */
export function useMediaQuery(query: string, defaultValue: boolean): boolean {
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => undefined;
      }
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );
  const getSnapshot = () =>
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? defaultValue
      : window.matchMedia(query).matches;
  return React.useSyncExternalStore(subscribe, getSnapshot, () => defaultValue);
}

/** The `md` breakpoint of `styles/theme.css`, as a min-width media query. */
export const MD_UP = '(min-width: 768px)';

/** The `lg` breakpoint of `styles/theme.css`, as a min-width media query. */
export const LG_UP = '(min-width: 1024px)';

/** The `sm` breakpoint of `styles/theme.css`, as a min-width media query. */
export const SM_UP = '(min-width: 640px)';
